import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PageShell } from "@/components/mai/PageShell";
import { useProduct } from "@/context/ProductContext";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { CircleNotch, ArrowRight, ShieldCheck, WarningCircle, CheckCircle, LockKey, X, ArrowClockwise } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { id: "draft",              label: "Draft" },
  { id: "internal_review",    label: "Internal review" },
  { id: "technical_review",   label: "Technical review" },
  { id: "country_validation", label: "Country validation" },
  { id: "final_approved",     label: "Final approved" },
];

const CATEGORY_LABEL = {
  A: "A · Critical claim without a verified source",
  B: "B · Mandatory country field missing",
  C: "C · Cross-module figure inconsistent",
  D: "D · Important duplicate unresolved",
  E: "E · Required reviewer approval missing",
  F: "F · Calculated value based on missing inputs",
};

const CATEGORY_ROUTE = {
  A: "/console/claims", B: "/console/countries",
  C: "/console/quality", D: "/console/merge-review",
  E: "/console/quality", F: "/console/report",
};

function StatusPipeline({ current, next, canRelease, watermarkRequired }) {
  return (
    <div data-testid="status-pipeline" className="bg-white border border-slate-200 p-4">
      <div className="flex items-center gap-1 md:gap-3 overflow-x-auto">
        {STATUSES.map((s, i) => {
          const currIdx = STATUSES.findIndex((x) => x.id === current);
          const isDone = i < currIdx;
          const isCurrent = i === currIdx;
          return (
            <div key={s.id} data-testid={`pipeline-${s.id}`}
              className="flex items-center gap-1 md:gap-3 shrink-0">
              <div className={`flex items-center gap-2 px-3 py-1.5 border ${
                isCurrent ? "bg-slate-900 text-white border-slate-900" :
                isDone ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                "bg-white text-slate-400 border-slate-200"}`}>
                {isDone && <CheckCircle size={12} weight="fill" />}
                <span className="text-[11px] font-semibold">{s.label}</span>
              </div>
              {i < STATUSES.length - 1 && <ArrowRight size={12} className="text-slate-300" />}
            </div>
          );
        })}
      </div>
      <div className="mt-3 flex items-center gap-3 text-[11px]">
        {watermarkRequired
          ? <span className="text-amber-700"><WarningCircle size={11} className="inline mr-1" weight="fill" /> DRAFT watermark will be applied to any export.</span>
          : <span className="text-emerald-700"><ShieldCheck size={11} className="inline mr-1" weight="fill" /> Exports carry no DRAFT watermark.</span>}
        {next && <span className="text-slate-500 ml-auto">Next stage: <b>{STATUSES.find((s) => s.id === next)?.label}</b></span>}
      </div>
    </div>
  );
}

function BlockerList({ blockers, byCategory }) {
  const grouped = useMemo(() => {
    const map = {};
    blockers.forEach((b) => { (map[b.category] = map[b.category] || []).push(b); });
    return map;
  }, [blockers]);
  if (blockers.length === 0) {
    return (
      <div data-testid="blockers-empty" className="bg-emerald-50 border border-emerald-200 p-4 flex items-center gap-3">
        <CheckCircle size={18} weight="fill" className="text-emerald-700" />
        <div>
          <p className="text-sm font-semibold text-emerald-800">No blocking issues detected.</p>
          <p className="text-[11px] text-emerald-700">The release checklist is clean. You may advance through the pipeline.</p>
        </div>
      </div>
    );
  }
  return (
    <div data-testid="blocker-list" className="space-y-3">
      {["A", "B", "C", "D", "E", "F"].filter((k) => (byCategory[k] || 0) > 0).map((cat) => (
        <section key={cat} data-testid={`blocker-cat-${cat}`} className="bg-white border border-red-100">
          <header className="px-4 py-2.5 bg-red-50/60 border-b border-red-100 flex items-center justify-between">
            <p className="overline-label text-red-700">{CATEGORY_LABEL[cat]}</p>
            <span className="text-[11px] font-semibold text-red-700">{grouped[cat].length} issue(s)</span>
          </header>
          <ul className="divide-y divide-slate-100 text-[11px]">
            {grouped[cat].map((b, i) => (
              <li key={i} data-testid={`blocker-${cat}-${i}`} className="px-4 py-2 text-slate-700 flex items-start gap-2">
                <span className="text-red-500 mt-0.5">•</span>
                <span className="flex-1">
                  {cat === "A" && <><b>{b.claim_category}</b>: {b.statement}</>}
                  {cat === "B" && <><b>{b.country_name}</b> — missing: {b.missing_fields.join(", ")}</>}
                  {cat === "C" && <><b>{b.field_label}</b>{b.country_id ? ` · ${b.country_id}` : ""} — sources: {b.sources.join(", ")}</>}
                  {cat === "D" && <><b>{b.name}</b> — duplicate of <code>{b.duplicate_of}</code></>}
                  {cat === "E" && <>Stage: <b>{b.stage}</b>{b.current_decision ? ` (${b.current_decision})` : ""}{b.reason ? ` — ${b.reason}` : ""}</>}
                  {cat === "F" && <>Field: <code>{b.field}</code></>}
                </span>
                <a href={CATEGORY_ROUTE[cat]} className="text-[10px] text-blue-600 hover:underline shrink-0">Fix in module →</a>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export default function ReleasePage({ embedded }) {
  const { productId, product } = useProduct();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(false);
  const [advNote, setAdvNote] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        axios.get(`${API}/release/${productId}`),
        axios.get(`${API}/release/${productId}/audit`),
      ]);
      setData(r.data); setAudit(a.data || []);
    } catch { toast.error("Failed to load release checklist"); }
    finally { setLoading(false); }
  }, [productId]);
  useEffect(() => { load(); }, [load]);

  const advance = async (senior_confirmation = false) => {
    if (!data?.next_status) return;
    setBusy("advance");
    try {
      await axios.post(`${API}/release/${productId}/advance`, {
        target: data.next_status, note: advNote, senior_confirmation,
      });
      toast.success(`Advanced to ${data.next_status.replace(/_/g, " ")}`);
      setAdvNote(""); setConfirmOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Advance failed"); }
    finally { setBusy(null); }
  };

  const reset = async () => {
    if (!window.confirm("Reset the release status back to draft?")) return;
    setBusy("reset");
    try {
      await axios.post(`${API}/release/${productId}/reset`, { note: advNote });
      toast.success("Status reset to draft"); setAdvNote("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
    finally { setBusy(null); }
  };

  const canAdvance = data && data.next_status &&
    (data.next_status === "internal_review" || data.can_release);
  const isFinal = data && data.current_status === "final_approved" && data.can_release;

  return (
    <PageShell embedded={embedded} movedTo={{ label: "Evidence Review", to: "/console/evidence-review?tab=release" }} pageId="release" overline={`External Release Controls · Prompt 13 · ${product?.name || ""}`}
      title="Release Checklist & Approval">
      {!data ? (
        <p data-testid="loading" className="py-16 text-center text-xs text-slate-400">Loading release checklist…</p>
      ) : (
        <>
          <StatusPipeline current={data.current_status} next={data.next_status}
            canRelease={data.can_release} watermarkRequired={data.watermark_required} />

          {isFinal && (
            <div data-testid="final-banner" className="bg-emerald-50 border border-emerald-200 px-4 py-3 flex items-center gap-3">
              <ShieldCheck size={18} className="text-emerald-700" weight="fill" />
              <div>
                <p className="text-sm font-semibold text-emerald-800">Final approved for external release.</p>
                <p className="text-[11px] text-emerald-700">Confirmed by {data.senior_approver_email} on {data.confirmed_at ? new Date(data.confirmed_at).toLocaleString() : ""}.</p>
              </div>
            </div>
          )}

          <BlockerList blockers={data.blockers || []} byCategory={data.by_category || {}} />

          <div className="bg-white border border-slate-200 p-4 space-y-2">
            <p className="overline-label text-slate-400">Advance / reset</p>
            <Textarea data-testid="advance-note" rows={2} className="text-xs"
              placeholder="Reviewer note (optional, stored on audit)"
              value={advNote} onChange={(e) => setAdvNote(e.target.value)} />
            <div className="flex flex-wrap items-center gap-2">
              {data.next_status && data.next_status !== "final_approved" && (
                <button data-testid="advance-btn" disabled={!canAdvance || busy === "advance"} onClick={() => advance(false)}
                  className="text-[11px] font-semibold text-white bg-slate-900 hover:bg-slate-800 px-3 py-2 disabled:opacity-40 flex items-center gap-1.5">
                  {busy === "advance" ? <CircleNotch size={11} className="animate-spin" /> : <ArrowRight size={11} />}
                  Advance to {STATUSES.find((s) => s.id === data.next_status)?.label}
                </button>
              )}
              {data.next_status === "final_approved" && (
                <button data-testid="final-approve-btn" disabled={!data.can_release || !isAdmin || busy === "advance"}
                  onClick={() => setConfirmOpen(true)}
                  className="text-[11px] font-semibold text-white bg-emerald-700 hover:bg-emerald-800 px-3 py-2 disabled:opacity-40 flex items-center gap-1.5">
                  <ShieldCheck size={11} /> Confirm external release (admin)
                </button>
              )}
              {isAdmin && data.current_status !== "draft" && (
                <button data-testid="reset-btn" onClick={reset} disabled={busy === "reset"}
                  className="ml-auto text-[11px] font-semibold text-red-700 border border-red-300 hover:bg-red-50 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1.5">
                  <ArrowClockwise size={11} /> Reset to draft
                </button>
              )}
            </div>
            {!isAdmin && data.next_status === "final_approved" && (
              <p data-testid="admin-only-hint" className="text-[11px] text-amber-700"><LockKey size={11} className="inline mr-1" /> Only a senior approver (admin role) can confirm the final external release.</p>
            )}
          </div>

          {audit.length > 0 && (
            <div className="bg-white border border-slate-200 p-4" data-testid="release-audit">
              <p className="overline-label text-slate-400">Release audit log</p>
              <ul className="mt-2 space-y-1 text-[11px]">
                {audit.map((a) => (
                  <li key={a.id} data-testid={`audit-${a.id}`} className="border-l-2 border-slate-200 pl-2 text-slate-600">
                    <b>{a.actor_email}</b> · <span className="text-slate-500">{a.action}</span>
                    {a.action === "advance" && <> · {a.from_status} → <span className="font-semibold">{a.to_status}</span></>}
                    <span className="text-slate-400"> · {new Date(a.at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirmOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white border border-slate-200 max-w-md w-full p-5 space-y-3" data-testid="final-confirm-dialog">
            <p className="font-heading font-bold text-sm">Confirm external release</p>
            <p className="text-xs text-slate-600">You are approving <b>{product?.name}</b> for external release. This action stamps your name and the current timestamp on the report envelope and removes the DRAFT watermark.</p>
            <Textarea data-testid="final-note" rows={3} className="text-xs" placeholder="Approval comment (optional)"
              value={advNote} onChange={(e) => setAdvNote(e.target.value)} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmOpen(false)} className="text-[11px] font-semibold text-slate-600 border border-slate-300 px-3 py-1.5 hover:bg-slate-50">Cancel</button>
              <button data-testid="final-confirm-submit" onClick={() => advance(true)} disabled={busy === "advance"}
                className="text-[11px] font-semibold text-white bg-emerald-700 hover:bg-emerald-800 px-3 py-1.5 disabled:opacity-50 flex items-center gap-1.5">
                {busy === "advance" ? <CircleNotch size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
                Confirm release
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
